import { useState, useEffect, useCallback } from "react";

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || "";
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || "";
const ADMIN_EMAIL = process.env.REACT_APP_OWNER_EMAIL || "";
const ADMIN_PASSWORD = process.env.REACT_APP_OWNER_PASSWORD || "";

// ─── SUPABASE HELPERS ─────────────────────────────────────────────────────────
const sb = {
  async getContent(section) {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/fj_site_content?section=eq.${section}&select=content`, {
      headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` }
    });
    const data = await res.json();
    return data?.[0]?.content || null;
  },
  async saveContent(section, content) {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/fj_site_content?section=eq.${section}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}`, Prefer: "return=minimal" },
      body: JSON.stringify({ content, updated_at: new Date().toISOString() })
    });
    return res.ok;
  },
  async getSubscribers() {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/fj_users?select=id,email,business_name,plan,demo_expires,created_at&order=created_at.desc&limit=100`, {
      headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` }
    });
    return res.ok ? await res.json() : [];
  },
  async getDemoRequests() {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/fj_demo_requests?select=*&order=created_at.desc&limit=100`, {
      headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` }
    });
    return res.ok ? await res.json() : [];
  }
};

// ─── CSS ──────────────────────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --ink: #0f1923; --gold: #c8972a; --gold-l: #e4ae3a; --gold-p: rgba(200,151,42,0.1);
    --gold-b: rgba(200,151,42,0.25); --emerald: #10b981; --amber: #f59e0b; --red: #ef4444;
    --white: #fff; --off: #f8f9fb; --off2: #f1f4f8; --border: #e4e9f0; --muted: #64748b; --text: #1a2535;
    --sidebar: 240px;
  }
  body { font-family: 'IBM Plex Sans', sans-serif; background: var(--off); color: var(--text); overflow-x: hidden; }
  button { font-family: 'IBM Plex Sans', sans-serif; cursor: pointer; }
  input, select, textarea { font-family: 'IBM Plex Sans', sans-serif; }

  /* LOGIN */
  .login-wrap { min-height: 100vh; display: flex; align-items: center; justify-content: center; background: var(--ink); }
  .login-box { background: white; border-radius: 20px; padding: 48px 44px; width: 100%; max-width: 420px; box-shadow: 0 32px 80px rgba(0,0,0,0.3); }
  .login-logo { display: flex; align-items: center; gap: 10px; margin-bottom: 32px; }
  .login-mark { width: 38px; height: 38px; border-radius: 10px; background: var(--ink); border: 1px solid var(--gold-b); display: flex; align-items: center; justify-content: center; }
  .login-mark svg { width: 20px; height: 20px; stroke: var(--gold); }
  .login-name { font-family: 'Playfair Display', serif; font-size: 22px; color: var(--ink); }
  .login-sub { font-size: 11px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--muted); margin-bottom: 2px; }
  .login-title { font-size: 24px; font-weight: 700; color: var(--ink); margin-bottom: 28px; }
  .field { margin-bottom: 18px; }
  .field label { display: block; font-size: 12px; font-weight: 600; color: var(--ink); margin-bottom: 6px; }
  .field input { width: 100%; padding: 11px 14px; border: 1.5px solid var(--border); border-radius: 8px; font-size: 14px; outline: none; transition: border-color 0.2s; }
  .field input:focus { border-color: var(--gold); }
  .login-err { background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.2); color: var(--red); font-size: 13px; padding: 10px 14px; border-radius: 8px; margin-bottom: 16px; }
  .btn-login { width: 100%; background: var(--ink); color: white; padding: 13px; border-radius: 10px; font-size: 14px; font-weight: 700; border: none; }
  .btn-login:hover { background: #162030; }

  /* LAYOUT */
  .admin-wrap { display: flex; min-height: 100vh; }
  .sidebar { width: var(--sidebar); background: var(--ink); position: fixed; top: 0; left: 0; bottom: 0; display: flex; flex-direction: column; z-index: 100; overflow-y: auto; }
  .sb-logo { display: flex; align-items: center; gap: 10px; padding: 20px 20px 18px; border-bottom: 1px solid rgba(255,255,255,0.07); }
  .sb-mark { width: 32px; height: 32px; border-radius: 8px; background: var(--gold-p); border: 1px solid var(--gold-b); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .sb-mark svg { width: 16px; height: 16px; stroke: var(--gold); }
  .sb-name { font-family: 'Playfair Display', serif; font-size: 17px; color: white; }
  .sb-badge { font-size: 9px; font-weight: 700; letter-spacing: 1px; color: var(--gold); background: var(--gold-p); border: 1px solid var(--gold-b); padding: 2px 8px; border-radius: 100px; margin-left: auto; }
  .sb-section { padding: 16px 12px 4px; font-size: 9px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: rgba(255,255,255,0.25); }
  .sb-item { display: flex; align-items: center; gap: 10px; padding: 10px 14px; margin: 2px 8px; border-radius: 9px; cursor: pointer; transition: all 0.15s; color: rgba(255,255,255,0.5); font-size: 13px; font-weight: 500; border: none; background: none; width: calc(100% - 16px); text-align: left; }
  .sb-item:hover { background: rgba(255,255,255,0.06); color: rgba(255,255,255,0.8); }
  .sb-item.active { background: var(--gold-p); border: 1px solid var(--gold-b); color: var(--gold); }
  .sb-item svg { width: 15px; height: 15px; stroke: currentColor; flex-shrink: 0; }
  .sb-bottom { margin-top: auto; padding: 12px 8px; border-top: 1px solid rgba(255,255,255,0.07); }
  .sb-user { display: flex; align-items: center; gap: 10px; padding: 10px 12px; }
  .sb-avatar { width: 30px; height: 30px; border-radius: 50%; background: var(--gold-p); border: 1px solid var(--gold-b); display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; color: var(--gold); flex-shrink: 0; }
  .sb-email { font-size: 11px; color: rgba(255,255,255,0.4); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .btn-logout { width: 100%; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.08); color: rgba(255,255,255,0.4); padding: 8px; border-radius: 8px; font-size: 12px; margin-top: 6px; }
  .btn-logout:hover { background: rgba(239,68,68,0.1); border-color: rgba(239,68,68,0.2); color: var(--red); }

  /* MAIN */
  .main { margin-left: var(--sidebar); flex: 1; display: flex; flex-direction: column; min-height: 100vh; }
  .topbar { background: white; border-bottom: 1px solid var(--border); padding: 0 32px; height: 60px; display: flex; align-items: center; justify-content: space-between; position: sticky; top: 0; z-index: 50; }
  .topbar-title { font-size: 16px; font-weight: 700; color: var(--ink); }
  .topbar-sub { font-size: 12px; color: var(--muted); margin-top: 2px; }
  .topbar-actions { display: flex; gap: 10px; align-items: center; }
  .content { padding: 32px; flex: 1; }

  /* BUTTONS */
  .btn { padding: 9px 20px; border-radius: 8px; font-size: 13px; font-weight: 700; border: none; transition: all 0.2s; }
  .btn-primary { background: var(--ink); color: white; }
  .btn-primary:hover { background: #162030; }
  .btn-gold { background: var(--gold); color: var(--ink); }
  .btn-gold:hover { background: var(--gold-l); }
  .btn-outline { background: white; color: var(--ink); border: 1.5px solid var(--border); }
  .btn-outline:hover { border-color: var(--ink); }
  .btn-danger { background: rgba(239,68,68,0.08); color: var(--red); border: 1px solid rgba(239,68,68,0.2); }
  .btn-danger:hover { background: rgba(239,68,68,0.15); }
  .btn-sm { padding: 6px 14px; font-size: 12px; }
  .btn-icon { width: 30px; height: 30px; padding: 0; display: inline-flex; align-items: center; justify-content: center; border-radius: 7px; }
  .btn-icon svg { width: 14px; height: 14px; stroke: currentColor; }

  /* CARDS */
  .card { background: white; border: 1px solid var(--border); border-radius: 14px; padding: 24px; }
  .card-title { font-size: 14px; font-weight: 700; color: var(--ink); margin-bottom: 4px; }
  .card-sub { font-size: 12px; color: var(--muted); margin-bottom: 20px; }

  /* FORM FIELDS */
  .f-group { margin-bottom: 18px; }
  .f-label { display: block; font-size: 12px; font-weight: 600; color: var(--ink); margin-bottom: 6px; letter-spacing: 0.2px; }
  .f-hint { font-size: 11px; color: var(--muted); margin-bottom: 6px; }
  .f-input { width: 100%; padding: 10px 12px; border: 1.5px solid var(--border); border-radius: 8px; font-size: 13.5px; color: var(--text); outline: none; transition: border-color 0.2s; background: white; }
  .f-input:focus { border-color: var(--gold); }
  .f-textarea { width: 100%; padding: 10px 12px; border: 1.5px solid var(--border); border-radius: 8px; font-size: 13.5px; color: var(--text); outline: none; resize: vertical; min-height: 80px; transition: border-color 0.2s; background: white; line-height: 1.6; }
  .f-textarea:focus { border-color: var(--gold); }
  .f-row { display: grid; gap: 16px; }
  .f-row.cols-2 { grid-template-columns: 1fr 1fr; }
  .f-row.cols-3 { grid-template-columns: 1fr 1fr 1fr; }

  /* SAVE BAR */
  .save-bar { position: sticky; bottom: 0; background: white; border-top: 1px solid var(--border); padding: 14px 32px; display: flex; align-items: center; justify-content: space-between; gap: 16px; z-index: 20; }
  .save-status { font-size: 13px; color: var(--muted); }
  .save-status.saved { color: var(--emerald); font-weight: 600; }
  .save-status.saving { color: var(--gold); }
  .save-status.error { color: var(--red); }

  /* LIST EDITOR (FAQ, blog, features, integrations, etc.) */
  .list-item { background: white; border: 1px solid var(--border); border-radius: 12px; padding: 18px 20px; margin-bottom: 12px; position: relative; }
  .list-item-header { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; }
  .list-item-num { width: 24px; height: 24px; border-radius: 6px; background: var(--off); border: 1px solid var(--border); display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; color: var(--muted); flex-shrink: 0; }
  .list-item-actions { display: flex; gap: 6px; margin-left: auto; }
  .add-item-btn { display: flex; align-items: center; gap: 8px; padding: 10px 16px; border: 1.5px dashed var(--border); border-radius: 10px; background: var(--off); color: var(--muted); font-size: 13px; font-weight: 600; width: 100%; justify-content: center; transition: all 0.2s; }
  .add-item-btn:hover { border-color: var(--gold); color: var(--gold); background: var(--gold-p); }

  /* PLAN CARDS EDITOR */
  .plan-editor-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
  .plan-editor-card { border: 1px solid var(--border); border-radius: 12px; padding: 18px; background: var(--off); }
  .plan-editor-card.highlighted { border-color: var(--gold); background: rgba(200,151,42,0.04); }
  .plan-editor-title { font-size: 13px; font-weight: 700; color: var(--ink); margin-bottom: 14px; display: flex; align-items: center; gap: 8px; }
  .highlight-toggle { display: flex; align-items: center; gap: 8px; margin-left: auto; cursor: pointer; }
  .highlight-toggle input { width: 14px; height: 14px; accent-color: var(--gold); }
  .highlight-toggle span { font-size: 11px; color: var(--muted); font-weight: 500; }

  /* FEATURE TAGS EDITOR */
  .tags-wrap { display: flex; flex-wrap: wrap; gap: 6px; padding: 8px; border: 1.5px solid var(--border); border-radius: 8px; min-height: 40px; cursor: text; background: white; }
  .tag { display: inline-flex; align-items: center; gap: 6px; background: var(--off2); border: 1px solid var(--border); border-radius: 6px; padding: 4px 10px; font-size: 12px; color: var(--text); }
  .tag-remove { background: none; border: none; padding: 0; line-height: 1; color: var(--muted); font-size: 14px; cursor: pointer; }
  .tag-remove:hover { color: var(--red); }
  .tag-input { border: none; outline: none; font-size: 12px; min-width: 100px; background: transparent; font-family: 'IBM Plex Sans', sans-serif; }

  /* SUBSCRIBERS TABLE */
  .data-table { width: 100%; border-collapse: collapse; }
  .data-table th { padding: 10px 14px; text-align: left; font-size: 11px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: var(--muted); border-bottom: 1px solid var(--border); background: var(--off); }
  .data-table td { padding: 12px 14px; font-size: 13px; color: var(--text); border-bottom: 1px solid var(--off2); }
  .data-table tr:last-child td { border-bottom: none; }
  .data-table tr:hover td { background: var(--off); }
  .plan-badge { display: inline-block; padding: 3px 10px; border-radius: 100px; font-size: 11px; font-weight: 700; }
  .plan-badge.paid { background: rgba(16,185,129,0.12); color: var(--emerald); }
  .plan-badge.trial { background: rgba(245,158,11,0.12); color: var(--amber); }
  .plan-badge.free { background: var(--off2); color: var(--muted); }
  .plan-badge.pending { background: rgba(200,151,42,0.12); color: var(--gold); }

  /* STATS STRIP */
  .stats-strip { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 28px; }
  .stat-card { background: white; border: 1px solid var(--border); border-radius: 12px; padding: 20px; }
  .stat-label { font-size: 11px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--muted); margin-bottom: 8px; }
  .stat-val { font-size: 28px; font-weight: 700; color: var(--ink); font-family: 'Playfair Display', serif; }
  .stat-sub { font-size: 12px; color: var(--muted); margin-top: 4px; }

  /* TOAST */
  .toast { position: fixed; bottom: 24px; right: 24px; background: var(--ink); color: white; padding: 12px 20px; border-radius: 10px; font-size: 13px; font-weight: 600; box-shadow: 0 8px 24px rgba(0,0,0,0.2); z-index: 999; animation: slideUp 0.25s ease; display: flex; align-items: center; gap: 10px; }
  .toast.success { background: var(--emerald); }
  .toast.error { background: var(--red); }
  @keyframes slideUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }

  /* SECTION TABS */
  .page-tabs { display: flex; gap: 4px; background: var(--off); border: 1px solid var(--border); border-radius: 10px; padding: 4px; margin-bottom: 24px; }
  .page-tab { padding: 7px 18px; border-radius: 7px; font-size: 13px; font-weight: 600; color: var(--muted); background: none; border: none; transition: all 0.15s; }
  .page-tab.active { background: white; color: var(--ink); box-shadow: 0 1px 4px rgba(0,0,0,0.08); }

  /* COLOUR SWATCH */
  .swatch-row { display: flex; gap: 8px; flex-wrap: wrap; }
  .swatch { width: 32px; height: 32px; border-radius: 8px; cursor: pointer; border: 2px solid transparent; transition: all 0.15s; }
  .swatch.selected { border-color: var(--gold); transform: scale(1.1); }
`;

// ─── TOAST ────────────────────────────────────────────────────────────────────
function Toast({ msg, type, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 2800); return () => clearTimeout(t); }, [onDone]);
  return <div className={`toast ${type}`}>{type === "success" ? "✓" : "✕"} {msg}</div>;
}

// ─── TAG INPUT ────────────────────────────────────────────────────────────────
function TagInput({ tags, onChange, placeholder = "Type and press Enter…" }) {
  const [val, setVal] = useState("");
  const add = () => { const v = val.trim(); if (v && !tags.includes(v)) { onChange([...tags, v]); setVal(""); } };
  return (
    <div className="tags-wrap" onClick={e => e.currentTarget.querySelector("input")?.focus()}>
      {tags.map((t, i) => (
        <span key={i} className="tag">
          {t}
          <button className="tag-remove" onClick={() => onChange(tags.filter((_, j) => j !== i))}>×</button>
        </span>
      ))}
      <input
        className="tag-input"
        value={val}
        onChange={e => setVal(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); add(); } if (e.key === "Backspace" && !val && tags.length) onChange(tags.slice(0, -1)); }}
        placeholder={tags.length === 0 ? placeholder : ""}
      />
    </div>
  );
}

// ─── SAVE BAR ─────────────────────────────────────────────────────────────────
function SaveBar({ status, onSave, dirty, loaded }) {
  return (
    <div className="save-bar">
      <span className={`save-status ${status}`}>
        {!loaded && "Loading content…"}
        {loaded && status === "saved" && "✓ All changes saved"}
        {loaded && status === "saving" && "Saving…"}
        {loaded && status === "error" && "✕ Save failed — try again"}
        {loaded && status === "idle" && (dirty ? "Unsaved changes" : "No changes")}
      </span>
      <button
        className="btn btn-gold"
        onClick={onSave}
        disabled={status === "saving" || !loaded}
        title={!loaded ? "Please wait — content is still loading" : ""}
      >
        {status === "saving" ? "Saving…" : !loaded ? "Loading…" : "Save Changes"}
      </button>
    </div>
  );
}

// ─── SECTION: HERO ────────────────────────────────────────────────────────────
function HeroEditor({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  return (
    <div className="card">
      <div className="card-title">Hero Section</div>
      <div className="card-sub">The first thing visitors see. Keep it punchy.</div>
      <div className="f-group">
        <label className="f-label">Tag line (small text above headline)</label>
        <input className="f-input" value={data.tag || ""} onChange={e => set("tag", e.target.value)} placeholder="South African Field Service Platform" />
      </div>
      <div className="f-row cols-2">
        <div className="f-group">
          <label className="f-label">Primary CTA Button</label>
          <input className="f-input" value={data.cta_primary || ""} onChange={e => set("cta_primary", e.target.value)} placeholder="Get Started" />
        </div>
        <div className="f-group">
          <label className="f-label">Secondary CTA Button</label>
          <input className="f-input" value={data.cta_secondary || ""} onChange={e => set("cta_secondary", e.target.value)} placeholder="View features" />
        </div>
      </div>
      <div className="f-group">
        <label className="f-label">Subtext paragraph</label>
        <textarea className="f-textarea" rows={3} value={data.subtext || ""} onChange={e => set("subtext", e.target.value)} placeholder="Jobs, quotes, invoices…" />
      </div>
    </div>
  );
}

// ─── SECTION: PRICING ─────────────────────────────────────────────────────────
function PricingEditor({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  const setPlan = (i, k, v) => {
    const plans = [...(data.plans || [])];
    plans[i] = { ...plans[i], [k]: v };
    set("plans", plans);
  };
  const plans = data.plans || [];
  return (
    <div>
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title">Section Text</div>
        <div className="f-row cols-2">
          <div className="f-group"><label className="f-label">Eyebrow</label><input className="f-input" value={data.eyebrow || ""} onChange={e => set("eyebrow", e.target.value)} /></div>
          <div className="f-group"><label className="f-label">Headline</label><input className="f-input" value={data.headline || ""} onChange={e => set("headline", e.target.value)} /></div>
        </div>
        <div className="f-group"><label className="f-label">Subtext</label><input className="f-input" value={data.subtext || ""} onChange={e => set("subtext", e.target.value)} /></div>
      </div>
      <div className="plan-editor-grid">
        {plans.map((plan, i) => (
          <div key={i} className={`plan-editor-card${plan.highlight ? " highlighted" : ""}`}>
            <div className="plan-editor-title">
              {plan.name || `Plan ${i + 1}`}
              <label className="highlight-toggle">
                <input type="checkbox" checked={!!plan.highlight} onChange={e => setPlan(i, "highlight", e.target.checked)} />
                <span>Highlighted</span>
              </label>
            </div>
            <div className="f-row cols-2">
              <div className="f-group"><label className="f-label">Plan Name</label><input className="f-input" value={plan.name || ""} onChange={e => setPlan(i, "name", e.target.value)} /></div>
              <div className="f-group"><label className="f-label">Price (number or "Custom")</label><input className="f-input" value={plan.price || ""} onChange={e => setPlan(i, "price", e.target.value)} /></div>
            </div>
            <div className="f-row cols-2">
              <div className="f-group"><label className="f-label">Period text</label><input className="f-input" value={plan.period || ""} onChange={e => setPlan(i, "period", e.target.value)} placeholder="per month" /></div>
              <div className="f-group"><label className="f-label">Badge text (leave blank for none)</label><input className="f-input" value={plan.badge || ""} onChange={e => setPlan(i, "badge", e.target.value)} placeholder="Most popular" /></div>
            </div>
            <div className="f-row cols-2">
              <div className="f-group"><label className="f-label">CTA Button Text</label><input className="f-input" value={plan.cta || ""} onChange={e => setPlan(i, "cta", e.target.value)} /></div>
              <div className="f-group">
                <label className="f-label">CTA Style</label>
                <select className="f-input" value={plan.cta_style || "outline"} onChange={e => setPlan(i, "cta_style", e.target.value)}>
                  <option value="outline">Outline (dark on dark bg)</option>
                  <option value="gold">Gold (for highlighted plan)</option>
                </select>
              </div>
            </div>
            <div className="f-group"><label className="f-label">Note below button</label><input className="f-input" value={plan.note || ""} onChange={e => setPlan(i, "note", e.target.value)} placeholder="7-day free trial" /></div>
            <div className="f-group">
              <label className="f-label">Features list</label>
              <div className="f-hint">Press Enter after each feature to add it. Backspace to remove last.</div>
              <TagInput tags={plan.features || []} onChange={v => setPlan(i, "features", v)} placeholder="Add feature…" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── SECTION: FEATURES ────────────────────────────────────────────────────────
function FeaturesEditor({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  const modules = data.modules || [];
  const setMod = (i, k, v) => {
    const m = [...modules];
    m[i] = { ...m[i], [k]: v };
    set("modules", m);
  };
  const addMod = () => set("modules", [...modules, { title: "", desc: "", icon: "jobs" }]);
  const removeMod = (i) => set("modules", modules.filter((_, j) => j !== i));
  const iconOptions = ["jobs","invoice","workers","inventory","expenses","compliance","cashflow","whatsapp","portal","report","dashboard","fuel"];
  return (
    <div>
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="f-row cols-3">
          <div className="f-group"><label className="f-label">Eyebrow</label><input className="f-input" value={data.eyebrow || ""} onChange={e => set("eyebrow", e.target.value)} /></div>
          <div className="f-group"><label className="f-label">Headline</label><input className="f-input" value={data.headline || ""} onChange={e => set("headline", e.target.value)} /></div>
          <div className="f-group"><label className="f-label">Subtext</label><input className="f-input" value={data.subtext || ""} onChange={e => set("subtext", e.target.value)} /></div>
        </div>
      </div>
      {modules.map((m, i) => (
        <div key={i} className="list-item">
          <div className="list-item-header">
            <div className="list-item-num">{i + 1}</div>
            <div style={{ fontWeight: 700, fontSize: 13, color: "var(--ink)" }}>{m.title || "New module"}</div>
            <div className="list-item-actions">
              <button className="btn btn-sm btn-danger btn-icon" onClick={() => removeMod(i)}>
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/></svg>
              </button>
            </div>
          </div>
          <div className="f-row cols-3">
            <div className="f-group"><label className="f-label">Module Name</label><input className="f-input" value={m.title} onChange={e => setMod(i, "title", e.target.value)} /></div>
            <div className="f-group"><label className="f-label">Icon</label>
              <select className="f-input" value={m.icon || "jobs"} onChange={e => setMod(i, "icon", e.target.value)}>
                {iconOptions.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div className="f-group"><label className="f-label">Description</label><input className="f-input" value={m.desc} onChange={e => setMod(i, "desc", e.target.value)} /></div>
          </div>
        </div>
      ))}
      <button className="add-item-btn" onClick={addMod}>+ Add Module</button>
    </div>
  );
}

// ─── SECTION: PLATFORMS ───────────────────────────────────────────────────────
function PlatformsEditor({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  const platforms = data.platforms || [];
  const setP = (i, k, v) => { const p = [...platforms]; p[i] = { ...p[i], [k]: v }; set("platforms", p); };
  const addP = () => set("platforms", [...platforms, { name: "", tagline: "", desc: "", available: "Coming soon", available_color: "amber", highlights: [] }]);
  const removeP = (i) => set("platforms", platforms.filter((_, j) => j !== i));
  return (
    <div>
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="f-row cols-3">
          <div className="f-group"><label className="f-label">Eyebrow</label><input className="f-input" value={data.eyebrow || ""} onChange={e => set("eyebrow", e.target.value)} /></div>
          <div className="f-group"><label className="f-label">Headline</label><input className="f-input" value={data.headline || ""} onChange={e => set("headline", e.target.value)} /></div>
          <div className="f-group"><label className="f-label">Subtext</label><input className="f-input" value={data.subtext || ""} onChange={e => set("subtext", e.target.value)} /></div>
        </div>
      </div>
      {platforms.map((p, i) => (
        <div key={i} className="list-item">
          <div className="list-item-header">
            <div className="list-item-num">{i + 1}</div>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{p.name || "New Platform"}</div>
            <div className="list-item-actions">
              <button className="btn btn-sm btn-danger btn-icon" onClick={() => removeP(i)}>
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/></svg>
              </button>
            </div>
          </div>
          <div className="f-row cols-2">
            <div className="f-group"><label className="f-label">Platform Name</label><input className="f-input" value={p.name} onChange={e => setP(i, "name", e.target.value)} /></div>
            <div className="f-group"><label className="f-label">Tagline</label><input className="f-input" value={p.tagline} onChange={e => setP(i, "tagline", e.target.value)} /></div>
          </div>
          <div className="f-row cols-2">
            <div className="f-group">
              <label className="f-label">Status</label>
              <select className="f-input" value={p.available} onChange={e => setP(i, "available", e.target.value)}>
                <option>Available now</option>
                <option>Coming soon</option>
                <option>Beta</option>
              </select>
            </div>
            <div className="f-group">
              <label className="f-label">Status colour</label>
              <select className="f-input" value={p.available_color} onChange={e => setP(i, "available_color", e.target.value)}>
                <option value="green">Green (live)</option>
                <option value="amber">Amber (coming soon)</option>
              </select>
            </div>
          </div>
          <div className="f-group"><label className="f-label">Description</label><textarea className="f-textarea" rows={2} value={p.desc} onChange={e => setP(i, "desc", e.target.value)} /></div>
          <div className="f-group">
            <label className="f-label">Highlights</label>
            <TagInput tags={p.highlights || []} onChange={v => setP(i, "highlights", v)} placeholder="Add highlight…" />
          </div>
        </div>
      ))}
      <button className="add-item-btn" onClick={addP}>+ Add Platform</button>
    </div>
  );
}

// ─── SECTION: INTEGRATIONS ────────────────────────────────────────────────────
function IntegrationsEditor({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  const items = data.integrations || [];
  const setI = (i, k, v) => { const arr = [...items]; arr[i] = { ...arr[i], [k]: v }; set("integrations", arr); };
  const addI = () => set("integrations", [...items, { name: "", category: "Communication", desc: "", status: "Live" }]);
  const removeI = (i) => set("integrations", items.filter((_, j) => j !== i));
  return (
    <div>
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="f-row cols-3">
          <div className="f-group"><label className="f-label">Eyebrow</label><input className="f-input" value={data.eyebrow || ""} onChange={e => set("eyebrow", e.target.value)} /></div>
          <div className="f-group"><label className="f-label">Headline</label><input className="f-input" value={data.headline || ""} onChange={e => set("headline", e.target.value)} /></div>
          <div className="f-group"><label className="f-label">Subtext</label><input className="f-input" value={data.subtext || ""} onChange={e => set("subtext", e.target.value)} /></div>
        </div>
      </div>
      {items.map((item, i) => (
        <div key={i} className="list-item">
          <div className="list-item-header">
            <div className="list-item-num">{i + 1}</div>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{item.name || "New Integration"}</div>
            <div className="list-item-actions">
              <button className="btn btn-sm btn-danger btn-icon" onClick={() => removeI(i)}>
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/></svg>
              </button>
            </div>
          </div>
          <div className="f-row cols-3">
            <div className="f-group"><label className="f-label">Name</label><input className="f-input" value={item.name} onChange={e => setI(i, "name", e.target.value)} /></div>
            <div className="f-group"><label className="f-label">Category</label><input className="f-input" value={item.category} onChange={e => setI(i, "category", e.target.value)} /></div>
            <div className="f-group">
              <label className="f-label">Status</label>
              <select className="f-input" value={item.status} onChange={e => setI(i, "status", e.target.value)}>
                <option>Live</option>
                <option>Coming soon</option>
                <option>Beta</option>
              </select>
            </div>
          </div>
          <div className="f-group"><label className="f-label">Description</label><input className="f-input" value={item.desc} onChange={e => setI(i, "desc", e.target.value)} /></div>
        </div>
      ))}
      <button className="add-item-btn" onClick={addI}>+ Add Integration</button>
    </div>
  );
}

// ─── SECTION: TEMPLATES ───────────────────────────────────────────────────────
function TemplatesEditor({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  const items = data.templates || [];
  const setT = (i, k, v) => { const arr = [...items]; arr[i] = { ...arr[i], [k]: v }; set("templates", arr); };
  const addT = () => set("templates", [...items, { name: "", category: "Invoice", desc: "", preview_color: "#0f1923" }]);
  const removeT = (i) => set("templates", items.filter((_, j) => j !== i));
  const COLORS = ["#0f1923","#1a3a5c","#1a5c3a","#c8972a","#5c1a1a","#3a1a5c","#2d4a2d"];
  return (
    <div>
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="f-row cols-3">
          <div className="f-group"><label className="f-label">Eyebrow</label><input className="f-input" value={data.eyebrow || ""} onChange={e => set("eyebrow", e.target.value)} /></div>
          <div className="f-group"><label className="f-label">Headline</label><input className="f-input" value={data.headline || ""} onChange={e => set("headline", e.target.value)} /></div>
          <div className="f-group"><label className="f-label">Subtext</label><input className="f-input" value={data.subtext || ""} onChange={e => set("subtext", e.target.value)} /></div>
        </div>
      </div>
      {items.map((item, i) => (
        <div key={i} className="list-item">
          <div className="list-item-header">
            <div style={{ width: 28, height: 28, borderRadius: 6, background: item.preview_color, border: "1px solid var(--border)" }}></div>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{item.name || "New Template"}</div>
            <div className="list-item-actions">
              <button className="btn btn-sm btn-danger btn-icon" onClick={() => removeT(i)}>
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/></svg>
              </button>
            </div>
          </div>
          <div className="f-row cols-2">
            <div className="f-group"><label className="f-label">Name</label><input className="f-input" value={item.name} onChange={e => setT(i, "name", e.target.value)} /></div>
            <div className="f-group">
              <label className="f-label">Category</label>
              <select className="f-input" value={item.category} onChange={e => setT(i, "category", e.target.value)}>
                {["Invoice","Quote","Report","Compliance","Other"].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="f-group"><label className="f-label">Description</label><input className="f-input" value={item.desc} onChange={e => setT(i, "desc", e.target.value)} /></div>
          <div className="f-row cols-2">
            <div className="f-group">
              <label className="f-label">File download URL (optional)</label>
              <div className="f-hint">Upload to Supabase storage (documents bucket) and paste the public URL here.</div>
              <input className="f-input" value={item.file_url || ""} onChange={e => setT(i, "file_url", e.target.value)} placeholder="https://...supabase.co/storage/v1/object/public/documents/..." />
            </div>
            <div className="f-group">
              <label className="f-label">File type label</label>
              <select className="f-input" value={item.file_type || "PDF"} onChange={e => setT(i, "file_type", e.target.value)}>
                <option>PDF</option>
                <option>Word document</option>
                <option>Excel spreadsheet</option>
                <option>Template</option>
              </select>
            </div>
          </div>
          <div className="f-group">
            <label className="f-label">Preview colour</label>
            <div className="swatch-row">
              {COLORS.map(c => <div key={c} className={`swatch${item.preview_color === c ? " selected" : ""}`} style={{ background: c }} onClick={() => setT(i, "preview_color", c)} />)}
            </div>
          </div>
        </div>
      ))}
      <button className="add-item-btn" onClick={addT}>+ Add Template</button>
    </div>
  );
}

// ─── SECTION: FAQ ─────────────────────────────────────────────────────────────
function FAQEditor({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  const items = data.questions || [];
  const setQ = (i, k, v) => { const arr = [...items]; arr[i] = { ...arr[i], [k]: v }; set("questions", arr); };
  const addQ = () => set("questions", [...items, { q: "", a: "" }]);
  const removeQ = (i) => set("questions", items.filter((_, j) => j !== i));
  return (
    <div>
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="f-row cols-3">
          <div className="f-group"><label className="f-label">Eyebrow</label><input className="f-input" value={data.eyebrow || ""} onChange={e => set("eyebrow", e.target.value)} /></div>
          <div className="f-group"><label className="f-label">Headline</label><input className="f-input" value={data.headline || ""} onChange={e => set("headline", e.target.value)} /></div>
          <div className="f-group"><label className="f-label">Subtext</label><input className="f-input" value={data.subtext || ""} onChange={e => set("subtext", e.target.value)} /></div>
        </div>
      </div>
      {items.map((item, i) => (
        <div key={i} className="list-item">
          <div className="list-item-header">
            <div className="list-item-num">{i + 1}</div>
            <div style={{ fontWeight: 600, fontSize: 13, color: "var(--ink)" }}>{item.q || "New question"}</div>
            <div className="list-item-actions">
              {i > 0 && <button className="btn btn-sm btn-outline btn-icon" onClick={() => { const arr=[...items]; [arr[i-1],arr[i]]=[arr[i],arr[i-1]]; set("questions",arr); }}><svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 15l-6-6-6 6"/></svg></button>}
              {i < items.length-1 && <button className="btn btn-sm btn-outline btn-icon" onClick={() => { const arr=[...items]; [arr[i],arr[i+1]]=[arr[i+1],arr[i]]; set("questions",arr); }}><svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg></button>}
              <button className="btn btn-sm btn-danger btn-icon" onClick={() => removeQ(i)}><svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/></svg></button>
            </div>
          </div>
          <div className="f-group"><label className="f-label">Question</label><input className="f-input" value={item.q} onChange={e => setQ(i, "q", e.target.value)} /></div>
          <div className="f-group"><label className="f-label">Answer</label><textarea className="f-textarea" rows={3} value={item.a} onChange={e => setQ(i, "a", e.target.value)} /></div>
        </div>
      ))}
      <button className="add-item-btn" onClick={addQ}>+ Add Question</button>
    </div>
  );
}

// ─── SECTION: BLOG ────────────────────────────────────────────────────────────
function BlogEditor({ data, onChange }) {
  const [editing, setEditing] = useState(null);
  const set = (k, v) => onChange({ ...data, [k]: v });
  const posts = data.posts || [];
  const setPost = (i, k, v) => { const arr = [...posts]; arr[i] = { ...arr[i], [k]: v }; set("posts", arr); };
  const addPost = () => {
    const newPost = { id: String(Date.now()), title: "", slug: "", date: new Date().toISOString().split("T")[0], category: "Business", excerpt: "", content: "" };
    set("posts", [...posts, newPost]);
    setEditing(posts.length);
  };
  const removePost = (i) => { set("posts", posts.filter((_, j) => j !== i)); if (editing === i) setEditing(null); };

  if (editing !== null && posts[editing]) {
    const post = posts[editing];
    return (
      <div>
        <button className="btn btn-outline btn-sm" style={{ marginBottom: 20, display: "flex", alignItems: "center", gap: 6 }} onClick={() => setEditing(null)}>
          <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}><path d="M19 12H5M12 5l-7 7 7 7" /></svg>
          Back to posts list
        </button>
        <div className="card">
          <div className="card-title">Editing: {post.title || "New Post"}</div>
          <div className="f-row cols-2">
            <div className="f-group"><label className="f-label">Title</label><input className="f-input" value={post.title} onChange={e => setPost(editing, "title", e.target.value)} /></div>
            <div className="f-group"><label className="f-label">Category</label><input className="f-input" value={post.category} onChange={e => setPost(editing, "category", e.target.value)} placeholder="Business, Tips, News…" /></div>
          </div>
          <div className="f-row cols-2">
            <div className="f-group"><label className="f-label">Date</label><input className="f-input" type="date" value={post.date} onChange={e => setPost(editing, "date", e.target.value)} /></div>
            <div className="f-group"><label className="f-label">Excerpt (shown in card)</label><input className="f-input" value={post.excerpt} onChange={e => setPost(editing, "excerpt", e.target.value)} /></div>
          </div>
          <div className="f-group">
            <label className="f-label">Cover image URL (optional)</label>
            <div className="f-hint">Paste a public image URL — from Supabase storage, Cloudflare Images, or any direct .jpg/.png/.webp link</div>
            <input className="f-input" value={post.image || ""} onChange={e => setPost(editing, "image", e.target.value)} placeholder="https://..." />
            {post.image && <img src={post.image} alt="preview" style={{ marginTop: 10, width: "100%", maxHeight: 160, objectFit: "cover", borderRadius: 8, border: "1px solid var(--border)" }} />}
          </div>
          <div className="f-group">
            <label className="f-label">Full article content</label>
            <div className="f-hint">Plain text. Press Enter for new paragraphs.</div>
            <textarea className="f-textarea" rows={16} value={post.content} onChange={e => setPost(editing, "content", e.target.value)} placeholder="Write your article here…" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="f-row cols-3">
          <div className="f-group"><label className="f-label">Eyebrow</label><input className="f-input" value={data.eyebrow || ""} onChange={e => set("eyebrow", e.target.value)} /></div>
          <div className="f-group"><label className="f-label">Headline</label><input className="f-input" value={data.headline || ""} onChange={e => set("headline", e.target.value)} /></div>
          <div className="f-group"><label className="f-label">Subtext</label><input className="f-input" value={data.subtext || ""} onChange={e => set("subtext", e.target.value)} /></div>
        </div>
      </div>
      {posts.length === 0 && (
        <div style={{ textAlign: "center", padding: "40px", color: "var(--muted)", background: "white", border: "1px solid var(--border)", borderRadius: 12 }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>✍</div>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>No blog posts yet</div>
          <div style={{ fontSize: 13 }}>Click "Add Post" to write your first article.</div>
        </div>
      )}
      {posts.map((post, i) => (
        <div key={post.id} className="list-item" style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "var(--ink)", marginBottom: 4 }}>{post.title || "Untitled post"}</div>
            <div style={{ fontSize: 12, color: "var(--muted)" }}>{post.category} · {post.date}</div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-sm btn-outline" onClick={() => setEditing(i)}>Edit</button>
            <button className="btn btn-sm btn-danger btn-icon" onClick={() => removePost(i)}><svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/></svg></button>
          </div>
        </div>
      ))}
      <button className="add-item-btn" onClick={addPost}>+ Add Post</button>
    </div>
  );
}

// ─── SECTION: CTA ─────────────────────────────────────────────────────────────
function CTAEditor({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  return (
    <div className="card">
      <div className="card-title">CTA Section (bottom of homepage)</div>
      <div className="f-row cols-2">
        <div className="f-group"><label className="f-label">Eyebrow</label><input className="f-input" value={data.eyebrow || ""} onChange={e => set("eyebrow", e.target.value)} /></div>
        <div className="f-group"><label className="f-label">Headline</label><input className="f-input" value={data.headline || ""} onChange={e => set("headline", e.target.value)} /></div>
      </div>
      <div className="f-group"><label className="f-label">Subtext</label><textarea className="f-textarea" rows={2} value={data.subtext || ""} onChange={e => set("subtext", e.target.value)} /></div>
      <div className="f-row cols-2">
        <div className="f-group"><label className="f-label">Primary CTA text</label><input className="f-input" value={data.cta_primary || ""} onChange={e => set("cta_primary", e.target.value)} /></div>
        <div className="f-group"><label className="f-label">Secondary CTA text</label><input className="f-input" value={data.cta_secondary || ""} onChange={e => set("cta_secondary", e.target.value)} /></div>
      </div>
    </div>
  );
}

// ─── SECTION: PREFOOTER ───────────────────────────────────────────────────────
function PrefooterEditor({ data, onChange }) {
  const cols = data.columns || [];
  const setCol = (i, k, v) => { const arr = [...cols]; arr[i] = { ...arr[i], [k]: v }; onChange({ ...data, columns: arr }); };
  const setLink = (ci, li, k, v) => {
    const arr = [...cols];
    const links = [...(arr[ci].links || [])];
    links[li] = { ...links[li], [k]: v };
    arr[ci] = { ...arr[ci], links };
    onChange({ ...data, columns: arr });
  };
  const addLink = (ci) => {
    const arr = [...cols];
    arr[ci] = { ...arr[ci], links: [...(arr[ci].links || []), { label: "", page: "" }] };
    onChange({ ...data, columns: arr });
  };
  const removeLink = (ci, li) => {
    const arr = [...cols];
    arr[ci] = { ...arr[ci], links: arr[ci].links.filter((_, j) => j !== li) };
    onChange({ ...data, columns: arr });
  };
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
      {cols.map((col, ci) => (
        <div key={ci} className="card">
          <div className="f-group"><label className="f-label">Column Heading</label><input className="f-input" value={col.heading || ""} onChange={e => setCol(ci, "heading", e.target.value)} /></div>
          {(col.links || []).map((link, li) => (
            <div key={li} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "flex-start" }}>
              <div style={{ flex: 1 }}>
                <input className="f-input" style={{ marginBottom: 4 }} value={link.label || ""} onChange={e => setLink(ci, li, "label", e.target.value)} placeholder="Label" />
                <input className="f-input" value={link.page || link.legal || ""} onChange={e => {
                  const v = e.target.value;
                  const isLegal = ["terms","privacy","cancellation"].includes(v);
                  setLink(ci, li, isLegal ? "legal" : "page", v);
                  if (isLegal) setLink(ci, li, "page", undefined);
                }} placeholder="page key or legal" />
              </div>
              <button className="btn btn-sm btn-danger btn-icon" style={{ marginTop: 2 }} onClick={() => removeLink(ci, li)}>×</button>
            </div>
          ))}
          <button className="add-item-btn" style={{ marginTop: 8 }} onClick={() => addLink(ci)}>+ Add link</button>
        </div>
      ))}
    </div>
  );
}

// ─── SUBSCRIBERS VIEW ─────────────────────────────────────────────────────────
function SubscribersView() {
  const [users, setUsers] = useState([]);
  const [demos, setDemos] = useState([]);
  const [tab, setTab] = useState("users");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([sb.getSubscribers(), sb.getDemoRequests()]).then(([u, d]) => {
      setUsers(u); setDemos(d); setLoading(false);
    });
  }, []);

  const paid = users.filter(u => u.plan === "paid").length;
  const trial = users.filter(u => u.plan !== "paid" && u.demo_expires).length;
  const pendingDemos = demos.filter(d => d.status === "Pending").length;

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "var(--muted)" }}>Loading…</div>;

  return (
    <div>
      <div className="stats-strip">
        <div className="stat-card"><div className="stat-label">Total Users</div><div className="stat-val">{users.length}</div></div>
        <div className="stat-card"><div className="stat-label">Paid</div><div className="stat-val" style={{ color: "var(--emerald)" }}>{paid}</div></div>
        <div className="stat-card"><div className="stat-label">On Trial</div><div className="stat-val" style={{ color: "var(--amber)" }}>{trial}</div></div>
        <div className="stat-card"><div className="stat-label">Demo Requests</div><div className="stat-val" style={{ color: "var(--gold)" }}>{pendingDemos} pending</div></div>
      </div>
      <div className="page-tabs">
        <button className={`page-tab${tab === "users" ? " active" : ""}`} onClick={() => setTab("users")}>Users ({users.length})</button>
        <button className={`page-tab${tab === "demos" ? " active" : ""}`} onClick={() => setTab("demos")}>Demo Requests ({demos.length})</button>
      </div>
      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        {tab === "users" && (
          <table className="data-table">
            <thead><tr><th>Business</th><th>Email</th><th>Plan</th><th>Trial Expires</th><th>Joined</th></tr></thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td style={{ fontWeight: 600 }}>{u.business_name || "—"}</td>
                  <td>{u.email}</td>
                  <td><span className={`plan-badge ${u.plan === "paid" ? "paid" : u.demo_expires ? "trial" : "free"}`}>{u.plan || "free"}</span></td>
                  <td>{u.demo_expires ? new Date(u.demo_expires).toLocaleDateString("en-ZA") : "—"}</td>
                  <td>{u.created_at ? new Date(u.created_at).toLocaleDateString("en-ZA") : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {tab === "demos" && (
          <table className="data-table">
            <thead><tr><th>Business</th><th>Name</th><th>Email</th><th>Phone</th><th>Industry</th><th>Status</th><th>Date</th></tr></thead>
            <tbody>
              {demos.map(d => (
                <tr key={d.id}>
                  <td style={{ fontWeight: 600 }}>{d.business_name}</td>
                  <td>{d.owner_name || "—"}</td>
                  <td>{d.email}</td>
                  <td>{d.phone || "—"}</td>
                  <td>{d.industry || "—"}</td>
                  <td><span className={`plan-badge ${d.status === "Pending" ? "pending" : d.status === "Approved" ? "paid" : "free"}`}>{d.status}</span></td>
                  <td>{d.submitted_date || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


// ─── SECTION: ABOUT ──────────────────────────────────────────────────────────
function AboutEditor({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  const values = data.values || [];
  const setV = (i, k, v) => { const arr = [...values]; arr[i] = { ...arr[i], [k]: v }; set("values", arr); };
  const addV = () => set("values", [...values, { title: "", desc: "" }]);
  const removeV = (i) => set("values", values.filter((_, j) => j !== i));
  return (
    <div>
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title">Hero Section</div>
        <div className="f-group"><label className="f-label">Headline</label><input className="f-input" value={data.headline || ""} onChange={e => set("headline", e.target.value)} placeholder="Built by contractors, for contractors." /></div>
        <div className="f-group"><label className="f-label">Lead paragraph</label><textarea className="f-textarea" rows={3} value={data.lead || ""} onChange={e => set("lead", e.target.value)} /></div>
        <div className="f-group"><label className="f-label">Origin card text</label><textarea className="f-textarea" rows={3} value={data.origin || ""} onChange={e => set("origin", e.target.value)} /></div>
      </div>
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title">The Problem Section</div>
        <div className="f-group"><label className="f-label">Headline</label><input className="f-input" value={data.problem_headline || ""} onChange={e => set("problem_headline", e.target.value)} /></div>
        <div className="f-group"><label className="f-label">Body text</label><textarea className="f-textarea" rows={4} value={data.problem_body || ""} onChange={e => set("problem_body", e.target.value)} /></div>
      </div>
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title">Values</div>
        {values.map((v, i) => (
          <div key={i} className="list-item">
            <div className="list-item-header">
              <div className="list-item-num">{i + 1}</div>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{v.title || "New value"}</div>
              <div className="list-item-actions">
                <button className="btn btn-sm btn-danger btn-icon" onClick={() => removeV(i)}><svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/></svg></button>
              </div>
            </div>
            <div className="f-row cols-2">
              <div className="f-group"><label className="f-label">Title</label><input className="f-input" value={v.title} onChange={e => setV(i, "title", e.target.value)} /></div>
              <div className="f-group"><label className="f-label">Description</label><input className="f-input" value={v.desc} onChange={e => setV(i, "desc", e.target.value)} /></div>
            </div>
          </div>
        ))}
        <button className="add-item-btn" onClick={addV}>+ Add Value</button>
      </div>
      <div className="card">
        <div className="card-title">CTA Section</div>
        <div className="f-row cols-2">
          <div className="f-group"><label className="f-label">Headline</label><input className="f-input" value={data.cta_headline || ""} onChange={e => set("cta_headline", e.target.value)} /></div>
          <div className="f-group"><label className="f-label">Subtext</label><input className="f-input" value={data.cta_subtext || ""} onChange={e => set("cta_subtext", e.target.value)} /></div>
        </div>
      </div>
    </div>
  );
}

// ─── SECTION: GUIDE ───────────────────────────────────────────────────────────
function GuideEditor({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  const sections = data.sections || [];
  const setS = (i, k, v) => { const arr = [...sections]; arr[i] = { ...arr[i], [k]: v }; set("sections", arr); };
  const addS = () => set("sections", [...sections, { title: "", description: "", tip: "", image: "" }]);
  const removeS = (i) => set("sections", sections.filter((_, j) => j !== i));
  const moveS = (i, dir) => { const arr = [...sections]; [arr[i], arr[i+dir]] = [arr[i+dir], arr[i]]; set("sections", arr); };
  return (
    <div>
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title">Page Header</div>
        <div className="f-row cols-3">
          <div className="f-group"><label className="f-label">Eyebrow</label><input className="f-input" value={data.eyebrow || ""} onChange={e => set("eyebrow", e.target.value)} placeholder="How to use FieldJob" /></div>
          <div className="f-group"><label className="f-label">Headline</label><input className="f-input" value={data.headline || ""} onChange={e => set("headline", e.target.value)} placeholder="Your step-by-step guide." /></div>
          <div className="f-group"><label className="f-label">Subtext</label><input className="f-input" value={data.subtext || ""} onChange={e => set("subtext", e.target.value)} /></div>
        </div>
      </div>
      {sections.map((s, i) => (
        <div key={i} className="list-item">
          <div className="list-item-header">
            <div className="list-item-num">{i + 1}</div>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{s.title || "New step"}</div>
            <div className="list-item-actions">
              {i > 0 && <button className="btn btn-sm btn-outline btn-icon" onClick={() => moveS(i, -1)}><svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 15l-6-6-6 6"/></svg></button>}
              {i < sections.length-1 && <button className="btn btn-sm btn-outline btn-icon" onClick={() => moveS(i, 1)}><svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg></button>}
              <button className="btn btn-sm btn-danger btn-icon" onClick={() => removeS(i)}><svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/></svg></button>
            </div>
          </div>
          <div className="f-row cols-2">
            <div className="f-group"><label className="f-label">Step Title</label><input className="f-input" value={s.title} onChange={e => setS(i, "title", e.target.value)} /></div>
            <div className="f-group"><label className="f-label">Screenshot URL (optional)</label><input className="f-input" value={s.image || ""} onChange={e => setS(i, "image", e.target.value)} placeholder="https://..." /></div>
          </div>
          <div className="f-group"><label className="f-label">Description</label><textarea className="f-textarea" rows={4} value={s.description} onChange={e => setS(i, "description", e.target.value)} placeholder="Explain this step in detail…" /></div>
          <div className="f-group"><label className="f-label">Tip (optional)</label><input className="f-input" value={s.tip || ""} onChange={e => setS(i, "tip", e.target.value)} placeholder="Pro tip for this step…" /></div>
          {s.image && <img src={s.image} alt="preview" style={{ marginTop: 8, width: "100%", maxHeight: 140, objectFit: "cover", borderRadius: 8, border: "1px solid var(--border)" }} />}
        </div>
      ))}
      <button className="add-item-btn" onClick={addS}>+ Add Step</button>
    </div>
  );
}

// ─── MAIN ADMIN APP ───────────────────────────────────────────────────────────
export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginErr, setLoginErr] = useState("");
  const [activeSection, setActiveSection] = useState("hero");
  const [cms, setCms] = useState({});
  const [cmsLoaded, setCmsLoaded] = useState(false);
  const [dirty, setDirty] = useState({});
  const [saveStatus, setSaveStatus] = useState({});
  const [toast, setToast] = useState(null);

  const handleLogin = () => {
    if (email === ADMIN_EMAIL && password === ADMIN_PASSWORD) {
      setAuthed(true);
    } else {
      setLoginErr("Invalid email or password.");
    }
  };

  // Load CMS on auth — use Promise.all so all sections resolve before a single setCms call.
  // The old forEach approach caused a race condition where sections resolving simultaneously
  // would overwrite each other's data in state, leading to blog posts being saved as empty.
  useEffect(() => {
    if (!authed) return;
    const sections = ["hero","pricing","features","platforms","integrations","templates","faq","blog","cta","prefooter","about","guide"];
    Promise.all(
      sections.map(s => sb.getContent(s).then(d => [s, d]))
    ).then(results => {
      const data = {};
      results.forEach(([s, d]) => { if (d) data[s] = d; });
      setCms(data); // single atomic update — no race condition
      setCmsLoaded(true);
    });
  }, [authed]);

  const updateCms = (section, data) => {
    setCms(prev => ({ ...prev, [section]: data }));
    setDirty(prev => ({ ...prev, [section]: true }));
    setSaveStatus(prev => ({ ...prev, [section]: "idle" }));
  };

  const save = useCallback(async (section) => {
    // Safety guard — never save a blog section with zero posts if it previously had posts.
    // This prevents an accidental blank save from wiping all posts.
    if (section === "blog") {
      const posts = cms[section]?.posts || [];
      const hadPosts = dirty[section]; // only dirty if user actually made a change
      if (posts.length === 0 && !hadPosts) {
        setToast({ msg: "Blog not saved — no posts loaded yet. Please wait for content to load fully.", type: "error" });
        return;
      }
    }
    setSaveStatus(prev => ({ ...prev, [section]: "saving" }));
    const ok = await sb.saveContent(section, cms[section]);
    setSaveStatus(prev => ({ ...prev, [section]: ok ? "saved" : "error" }));
    setDirty(prev => ({ ...prev, [section]: !ok }));
    setToast({ msg: ok ? "Saved — changes are live on the website" : "Save failed. Try again.", type: ok ? "success" : "error" });
    if (ok) setTimeout(() => setSaveStatus(prev => ({ ...prev, [section]: "idle" })), 3000);
  }, [cms, dirty]);

  if (!authed) {
    return (
      <>
        <style>{css}</style>
        <div className="login-wrap">
          <div className="login-box">
            <div className="login-logo">
              <div className="login-mark">
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
                </svg>
              </div>
              <span className="login-name">FieldJob</span>
            </div>
            <div className="login-sub">Admin Panel</div>
            <div className="login-title">Sign in</div>
            {loginErr && <div className="login-err">{loginErr}</div>}
            <div className="field"><label>Email</label><input type="email" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && handleLogin()} /></div>
            <div className="field"><label>Password</label><input type="password" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && handleLogin()} /></div>
            <button className="btn-login" onClick={handleLogin}>Sign In</button>
          </div>
        </div>
      </>
    );
  }

  const navItems = [
    { section: "hero", label: "Hero", icon: <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /> },
    { section: "pricing", label: "Pricing", icon: <path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /> },
    { section: "features", label: "Features", icon: <><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></> },
    { section: "platforms", label: "Platforms", icon: <><rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></> },
    { section: "integrations", label: "Integrations", icon: <><circle cx="18" cy="18" r="3" /><circle cx="6" cy="6" r="3" /><path d="M13 6h3a2 2 0 0 1 2 2v7M11 18H8a2 2 0 0 1-2-2V9" /></> },
    { section: "templates", label: "Templates", icon: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></> },
    { section: "faq", label: "FAQ", icon: <><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" /></> },
    { section: "blog", label: "Blog", icon: <><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" /></> },
    { section: "cta", label: "CTA Section", icon: <><polyline points="13 17 18 12 13 7" /><polyline points="6 17 11 12 6 7" /></> },
    { section: "prefooter", label: "Footer Nav", icon: <><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="18" x2="21" y2="18" /></> },
    { section: "about", label: "About Us", icon: <><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></> },
    { section: "guide", label: "Instructions", icon: <><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></> },
    { section: "subscribers", label: "Subscribers", icon: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></> },
  ];

  const sectionTitles = {
    hero: "Hero Section", pricing: "Pricing", features: "Features Page", platforms: "Platforms Page",
    integrations: "Integrations Page", templates: "Templates Page", faq: "FAQ Page", blog: "Blog",
    cta: "CTA Section", prefooter: "Footer Navigation", subscribers: "Subscribers & Demos"
  };

  const isCmsSection = activeSection !== "subscribers";
  const sectionData = cms[activeSection] || {};

  return (
    <>
      <style>{css}</style>
      <div className="admin-wrap">
        {/* SIDEBAR */}
        <div className="sidebar">
          <div className="sb-logo">
            <div className="sb-mark">
              <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
            </div>
            <span className="sb-name">FieldJob</span>
            <span className="sb-badge">ADMIN</span>
          </div>
          <div className="sb-section">Content</div>
          {navItems.slice(0, 12).map(item => (
            <button key={item.section} className={`sb-item${activeSection === item.section ? " active" : ""}`} onClick={() => setActiveSection(item.section)}>
              <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{item.icon}</svg>
              {item.label}
              {dirty[item.section] && <span style={{ marginLeft: "auto", width: 7, height: 7, borderRadius: "50%", background: "var(--gold)", flexShrink: 0 }}></span>}
            </button>
          ))}
          <div className="sb-section">Business</div>
          <button className={`sb-item${activeSection === "subscribers" ? " active" : ""}`} onClick={() => setActiveSection("subscribers")}>
            <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{navItems[10].icon}</svg>
            Subscribers
          </button>
          <div className="sb-bottom">
            <div className="sb-user">
              <div className="sb-avatar">A</div>
              <div className="sb-email">{ADMIN_EMAIL}</div>
            </div>
            <button className="btn-logout" onClick={() => setAuthed(false)}>Sign out</button>
          </div>
        </div>

        {/* MAIN */}
        <div className="main">
          <div className="topbar">
            <div>
              <div className="topbar-title">{sectionTitles[activeSection]}</div>
              <div className="topbar-sub">Changes save to Supabase and go live on the marketing site instantly</div>
            </div>
            <div className="topbar-actions">
              <a href="https://fieldjob.getdiswayne.com" target="_blank" rel="noreferrer" className="btn btn-outline btn-sm" style={{ textDecoration: "none" }}>↗ View Site</a>
            </div>
          </div>

          <div className="content">
            {activeSection === "subscribers" && <SubscribersView />}
            {activeSection === "hero" && <HeroEditor data={sectionData} onChange={d => updateCms("hero", d)} />}
            {activeSection === "pricing" && <PricingEditor data={sectionData} onChange={d => updateCms("pricing", d)} />}
            {activeSection === "features" && <FeaturesEditor data={sectionData} onChange={d => updateCms("features", d)} />}
            {activeSection === "platforms" && <PlatformsEditor data={sectionData} onChange={d => updateCms("platforms", d)} />}
            {activeSection === "integrations" && <IntegrationsEditor data={sectionData} onChange={d => updateCms("integrations", d)} />}
            {activeSection === "templates" && <TemplatesEditor data={sectionData} onChange={d => updateCms("templates", d)} />}
            {activeSection === "faq" && <FAQEditor data={sectionData} onChange={d => updateCms("faq", d)} />}
            {activeSection === "blog" && <BlogEditor data={sectionData} onChange={d => updateCms("blog", d)} />}
            {activeSection === "cta" && <CTAEditor data={sectionData} onChange={d => updateCms("cta", d)} />}
            {activeSection === "prefooter" && <PrefooterEditor data={sectionData} onChange={d => updateCms("prefooter", d)} />}
            {activeSection === "about"     && <AboutEditor data={sectionData} onChange={d => updateCms("about", d)} />}
            {activeSection === "guide"     && <GuideEditor data={sectionData} onChange={d => updateCms("guide", d)} />}
          </div>

          {isCmsSection && (
            <SaveBar
              status={saveStatus[activeSection] || "idle"}
              dirty={!!dirty[activeSection]}
              onSave={() => save(activeSection)}
              loaded={cmsLoaded}
            />
          )}
        </div>
      </div>

      {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
    </>
  );
}
